import { createContext } from "react";

type MeetingPageContextValueType = {
  sidebarMeetingView?: "overview" | "load-previous-meeting" | number;
  setSidebarMeetingView?: (
    view: "overview" | "load-previous-meeting" | number,
  ) => void;
  onChangeMeetingView?: (
    meetingView: string,
    isReviewMeeting?: boolean | null,
  ) => void;
  isInSidebar: boolean;
  isInExtension: boolean;
  // When set (e.g. the profile 1-on-1 tab embeds the meeting page), the
  // prev/next meeting arrows paginate within the embedder instead of
  // navigating to a new meeting page. `position` is relative to the meeting
  // whose arrow was clicked: "before" = newer (above), "after" = older (below).
  onNavigateToMeeting?: (
    target: { id: number; meetingGroupId: number },
    position: "before" | "after",
  ) => void;
  // When set (the profile 1-on-1 tab embeds the meeting page for a specific
  // person), the prev/next meeting arrows page through the 1-on-1s where that
  // person is the SUBJECT -- across every MeetingGroup of the relationship --
  // instead of paging within the current meeting's MeetingGroup. Keeps the
  // navigable series in lockstep with the subject-scoped list the tab shows.
  oneononeSubjectId?: number | null;
  // Autofocus the default topic input on mount. Disabled when many meetings
  // render at once (the profile 1-on-1 tab) since the focus query is
  // document-wide and would steal focus / scroll the page.
  autoFocusDefaultTopic?: boolean;
  // Animate topic position changes (framer-motion layout). Disabled when
  // meetings stack in a paginated list: inserting one above shifts the topics
  // below, and the layout animation would make them all slide at once.
  animateTopicLayout?: boolean;
  // Meeting whose row is currently fetching its GraphQL data after being
  // added to the page (chevron click, calendar select, TOC line click).
  // Inline chevrons swap their icon for a spinner when their target id
  // matches this, mirroring the floating TOC's behavior.
  loadingMeetingId?: number | null;
  // Whether the meeting's Ask AI sidebar is expanded. Layout.Main's top
  // padding grows at the `xl:` breakpoint when expanded but at `md:` when
  // collapsed, so the sticky meeting header (details.tsx) reads this to switch
  // its negative-top cancellation to the matching breakpoint. Only the full
  // meeting page has this sidebar; embedded contexts leave it false.
  meetingSidebarExpanded?: boolean;
};

export const MeetingPageContext = createContext<MeetingPageContextValueType>({
  sidebarMeetingView: undefined,
  setSidebarMeetingView: undefined,
  onChangeMeetingView: undefined,
  isInSidebar: false,
  isInExtension: false,
  onNavigateToMeeting: undefined,
  oneononeSubjectId: undefined,
  autoFocusDefaultTopic: undefined,
  animateTopicLayout: undefined,
});
